/**
 * Applications Tab Prompt Builder
 *
 * Generates an integration topology diagram showing application nodes
 * and their connection links.
 */

import type { BulkEAImport } from '../../canister-client';
import type { ChatMessage } from '../../canister-client';
import { baseSvgRules, summarizeEntities } from './base-svg-prompt';

export function buildApplicationsPrompt(data: BulkEAImport): ChatMessage[] {
    const apps = summarizeEntities(data.applications, [
        'name', 'vendor', 'app_type', 'domain', 'users', 'criticality',
        'integration_method',
    ]);
    const integrations = data.applications
        .filter(a => Array.isArray(a.integrates_with) && a.integrates_with.length > 0)
        .map(a => `${a.name} -> ${(a.integrates_with as string[]).join(', ')}`)
        .join('\n');

    return [
        {
            role: 'user',
            content: `${baseSvgRules()}

DIAGRAM TYPE: Application Integration Topology

DATA - Applications:
${apps}

DATA - Integration Links:
${integrations || '(derive from relationships)'}

INSTRUCTIONS:
- Show each application as a node (rounded rectangle).
- Color-code nodes by criticality: red=Critical, amber=High, blue=Medium, green=Low.
- Draw directional arrows between applications that integrate with each other.
- Label arrows with the integration method if available.
- Group applications by domain into faint dashed-border regions.
- Show user count as a small badge on each node.
- Use a force-directed-style layout with central high-criticality apps.
- Add a title "Application Integration Map" at the top.
- Include a legend for criticality colors.`,
        },
    ];
}
